<template>
    <side-panel
        :show.sync="showPanel"
        :title="`${title} (${selectedItemList.length})`"
        :confirm-text="confirmText"
        :disabled="!selectedItems.length"
        cancel-text="Cancel"
        cancel-button-class="btn btn-light mr-2"
        confirm-button-class="btn btn-dark btn-block"
        @confirm="confirmExport"
    >
        <div v-if="selectedItemList.length === 0" class="text-center p-4">
            <p class="text-muted" v-if="isUser">No users selected</p>
            <p class="text-muted" v-else>No items selected</p>
        </div>
        <div
            v-else
            class="selected-users-list overflow-auto"
            id="selected-users-list"
        >
            <template v-if="!isUser">
                <!-- Parent Items -->
                <template v-for="parentItem in groupedItems">
                    <div :key="parentItem.item_id" class="user-item">
                        <input
                            type="checkbox"
                            :value="parentItem.item_id"
                            :checked="
                                selectedItems.includes(parentItem.item_id)
                            "
                            @change="
                                e =>
                                    handleItemSelection(
                                        parentItem,
                                        e.target.checked
                                    )
                            "
                            class="cursor-pointer mr-2"
                        />
                        <div class="user-info">
                            <div class="user-name">
                                <item-name :item="parentItem" :new-tab="true" />
                            </div>
                        </div>
                    </div>
                    <!-- Child Items -->
                    <div
                        v-for="childItem in parentItem.children"
                        :key="childItem.item_id"
                        class="user-item child-item"
                    >
                        <div class="user-info">
                            <div class="user-name">
                                {{ childItem.item_description }}
                            </div>
                        </div>
                    </div>
                </template>
            </template>
            <!-- User Items (unchanged) -->
            <template v-else>
                <div
                    v-for="item in selectedItemList"
                    :key="item.id"
                    class="user-item"
                >
                    <input
                        type="checkbox"
                        :value="item.id"
                        :checked="selectedItems.includes(item.id)"
                        @change="
                            e => handleItemSelection(item, e.target.checked)
                        "
                        class="cursor-pointer mr-2"
                    />
                    <div class="user-info">
                        <div class="user-name">
                            <actor-name
                                :actor="item"
                                :use-encoded="true"
                                :new-tab="true"
                            />
                        </div>
                        <div class="user-email text-muted">
                            {{ item.email }}
                        </div>
                    </div>
                </div>
            </template>
            <scroll-to-top
                v-if="value"
                :is-smooth="true"
                target-selector="side-panel-content"
                :bottom="90"
            />
        </div>
    </side-panel>
</template>

<script>
import SidePanel from "@/components/commons/side-panel";
import ActorName from "@/components/commons/actor-name";
import ItemName from "@/components/commons/item-name";
import ScrollToTop from "@/components/commons/scroll-to-top";
export default {
    name: "CubexSidePanel",
    components: {
        SidePanel,
        ActorName,
        ItemName,
        ScrollToTop
    },
    props: {
        value: {
            type: Boolean,
            default: false
        },
        items: {
            type: Array,
            required: true
        },
        title: {
            type: String,
            required: true
        },
        isUser: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            confirmText: "Download"
        };
    },
    computed: {
        showPanel: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit("input", value);
            }
        },
        selectedItemList() {
            return this.items.filter(item =>
                this.selectedItems.includes(item.id || item.item_id)
            );
        },
        selectedItems: {
            get() {
                return this.isUser 
                    ? this.$store.getters["cubex/selectedUsers"]
                    : this.$store.getters["cubex/selectedItems"];
            },
            set(value) {
                if (this.isUser) {
                    this.$store.dispatch("cubex/setSelectedUsers", value);
                } else {
                    this.$store.dispatch("cubex/setSelectedItems", value);
                }
            }
        },
        groupedItems() {
            if (this.isUser) return [];

            // Get all selected items
            const selectedItems = [...this.selectedItemList];

            // Separate parents and orphaned children
            const parentItems = selectedItems.filter(
                item => item.parent_id === null
            );

            const orphanedChildren = selectedItems.filter(
                item =>
                    item.parent_id !== null &&
                    !selectedItems.some(
                        parent => parent.item_id === item.parent_id
                    )
            );

            // Group children under their parents
            const groupedParents = parentItems.map(parent => ({
                ...parent,
                children: selectedItems.filter(
                    item => item.parent_id === parent.item_id
                )
            }));

            // Add orphaned children as top-level items
            return [
                ...groupedParents,
                ...orphanedChildren.map(child => ({
                    ...child,
                    children: []
                }))
            ];
        }
    },
    watch: {
        selectedItems: {
            handler(newVal) {
                if (!this.areArraysEqual(newVal, this.selectedItems)) {
                    this.$emit("update:selected", newVal);
                }

                if (this.selectedItems.length === 0) {
                    this.$emit("input", false);
                }
            }
        }
    },
    methods: {
        areArraysEqual(arr1, arr2) {
            if (arr1.length !== arr2.length) return false;
            return arr1.every(item => arr2.includes(item));
        },
        confirmExport() {
            this.$emit("confirm", this.selectedItems);
            this.$emit("input", false);
        },
        handleItemSelection(item, isChecked) {
            if (!this.isUser && item.parent_id === null) {
                // Find all child items
                const childItems = this.items.filter(
                    i => i.parent_id === item.item_id
                );

                if (isChecked) {
                    // Add parent and all children
                    const itemsToAdd = [
                        item.item_id || item.id,
                        ...childItems.map(child => child.item_id || child.id)
                    ];
                    this.selectedItems = [
                        ...new Set([...this.selectedItems, ...itemsToAdd])
                    ];
                } else {
                    // Remove parent and all children
                    const itemsToRemove = [
                        item.item_id || item.id,
                        ...childItems.map(child => child.item_id || child.id)
                    ];
                    this.selectedItems = this.selectedItems.filter(
                        id => !itemsToRemove.includes(id)
                    );
                }
            } else {
                // Handle regular item/user selection
                const itemId = item.item_id || item.id;
                if (isChecked) {
                    if (!this.selectedItems.includes(itemId)) {
                        this.selectedItems.push(itemId);
                    }
                } else {
                    const index = this.selectedItems.indexOf(itemId);
                    if (index > -1) {
                        this.selectedItems.splice(index, 1);
                    }
                }
            }
        }
    }
};
</script>

<style scoped>
.selected-users-list {
    padding: 1rem;
}

.user-item {
    display: flex;
    align-items: center;
    padding: 0.75rem;
    border-bottom: 1px solid #eee;
}

.user-item:last-child {
    border-bottom: none;
}

.child-item {
    padding-left: 2rem;
}

.user-info {
    flex: 1;
}

.user-email {
    font-size: 0.9em;
}
</style>
